import { ReactNode } from 'react';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { FormSettingsTypeEnum } from 'src/modules/member/models/formSettingsType.enum';
import { FormSettingField } from 'src/shared/services/memberFormSettingsService';
import { MemberAdvancedSearchCounty } from './memberAdvancedSearchCounty';
import { MemberAdvancedSearchGender } from './memberAdvancedSearchGender';
import { MemberAdvancedSearchExclusiveToggleButton } from './memberAdvancedSearchExclusiveToggleButton';
import { useMemberFormFields } from '../hooks/useMemberFormFields';

// HELT TYP-DRIVEN sektion (grupp B): itererar formulärets fält och renderar en sökkontroll för VARJE
// fält som är show=true – på samma sätt som "Redigera medlem" (dynamicFormField) gör via
// FormSettingsTypeEnum. Ingen fördefinierad registry-lista längre, så inga fält tappas (t.ex. tidning).
// Specialfält styrs på fieldName (län/kommun, distrikt, kön, personnummer, custom, tidning) FÖRE typ,
// övriga renderas per typ. Lokalförening, har/saknar-toggles och födelsedatum-intervall ligger i den
// fasta Kontaktinformation-sektionen (grupp A). otherContactMode styr vilket formulär som läses:
// 'other' för intressentregistret, annars 'member'. Etiketter från formulärets heading (i18n-fallback).
type MemberAdvancedSearchMemberDataProps = {
  otherContactMode: boolean;
};

// Matchar custom-fält custom1..custom10.
const CUSTOM_FIELD_REGEX = /^custom([1-9]|10)$/;

// Tidningsfältet (primärt). Backend/formuläret använder casingen 'newsPaper'.
const NEWSPAPER_REGEX = /^newspaper$/i;

// Alla sökfält renderas lika breda: 4 per rad på breda skärmar.
const fieldGrid = { xs: 12, sm: 6, md: 4, lg: 3 } as const;

// Fält som aldrig ska ge en sökkontroll här (interna, saknar rimlig värdesök eller ligger i grupp A).
// Distrikt (countyAss_id) och lokalförening (localAss_id) döljs precis som i redigerings-/skapa-modalen
// (deras hiddenFormKeys) och hanteras istället av grupp A/Kontaktinformation.
const SKIP_FIELD_NAMES = new Set(['localAss', 'localAss_id', 'countyAss_id', 'district', 'comment', 'note', 'password', 'userName']);

// Typer som aldrig renderas (rubriker och dolda fält).
const SKIP_TYPES = new Set<number>([FormSettingsTypeEnum.Label, FormSettingsTypeEnum.Header, FormSettingsTypeEnum.Hidden]);

// Textfält vars fieldName skiljer sig från RHF-namnet i sökfiltret (birthDate hanteras som specialfält).
const NAME_MAP: Record<string, string> = { COAddress: 'co', postNr: 'postCode', postAddress: 'city' };

// Länder som ska ligga överst i lands-droplistan (samma prioritetsordning som medlemsformuläret).
const PRIORITY_COUNTRIES = ['Sverige', 'Norge', 'Danmark', 'Finland'];

// RHF-fält som har ett befintligt backend-filter (MemberOptions/GetPage). Ett generiskt fält vars
// (mappade) namn INTE finns här renderas inte — vi lägger inte till nya API-filter i den här omgången.
const FILTER_FIELDS = new Set<string>([
  'firstName',
  'lastName',
  'pnr',
  'co',
  'country',
  'email',
  'phone',
  'phoneOther',
  'address',
  'addressOther',
  'city',
  'postCode',
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
]);

export const MemberAdvancedSearchMemberData = (props: MemberAdvancedSearchMemberDataProps) => {
  const { otherContactMode } = props;
  const { t } = useTranslation(['member', 'common']);
  const { fields, loading } = useMemberFormFields(otherContactMode ? 'other' : 'member');
  const { control } = useFormContext();

  // Vänta in formulärinställningarna innan något renderas (undviker felaktigt tomma/blinkande fält).
  if (loading) return null;

  // Etikett = formulärets heading om satt, annars i18n-nyckeln member:memberAdvancedSearch.<key>
  // (fallbackKey styr vilken nyckel som slås upp), och sist själva nyckeln/fieldName om ingen översättning finns.
  const labelFor = (field: FormSettingField, fallbackKey?: string) => {
    const h = field.heading?.trim();
    if (h && h.length > 0) return h;
    const key = fallbackKey ?? field.fieldName;
    const i18nKey = `member:memberAdvancedSearch.${key}`;
    const translated = t(i18nKey);
    return translated === i18nKey ? key : translated;
  };

  // Ett värdesök-textfält bundet till ett RHF-fält (fallback-etikett = RHF-namnet).
  const text = (field: FormSettingField, rhfName: string) => <ControlledTextField name={rhfName} label={labelFor(field, rhfName)} />;

  // Bygger droplista-alternativ likadant som dynamicFormField (keyValueArray: { value, label }).
  const toSelectableItems = (extraOptions?: { name: string; value: string }[]): SelectableItem[] =>
    (extraOptions ?? []).filter((o) => o.name !== '').map((o) => ({ value: o.value, label: o.name }));

  // Sökbar droplista (MUI Autocomplete, som betalmetod-/läns-fälten): typeahead, rensa-kryss och
  // placeholder "Alla". Tomt värde = inget filter = alla medlemmar oavsett sparat värde.
  const selectBox = (rhfName: string, label: string, options: SelectableItem[]): ReactNode => (
    <Controller
      name={rhfName}
      control={control}
      render={({ field: rhfField }) => (
        <Autocomplete
          options={options}
          size='small'
          fullWidth
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          value={options.find((o) => o.value === rhfField.value) ?? null}
          onChange={(_, newValue) => rhfField.onChange(newValue ? newValue.value : '')}
          renderInput={(params) => <TextField {...params} label={label} placeholder={t('common:form.all')} InputLabelProps={{ shrink: true }} />}
        />
      )}
    />
  );

  // Ett custom-fält renderas som droplista (exakt matchning) om det har alternativ, annars som textfält.
  const renderCustomField = (field: FormSettingField) => {
    const label = labelFor(field);
    const options = toSelectableItems(field.extraOptions);
    if (options.length > 0) return selectBox(field.fieldName, label, options);
    return <ControlledTextField name={field.fieldName} label={label} />;
  };

  // Lands-sök bunden till RHF 'country' (det BEFINTLIGA Country-filtret). Alternativen tas från
  // fältets extraOptions – samma källa som medlemsformuläret bygger sin lands-droplista av. Om
  // extraOptions innehåller länderna renderas en droplista (nordiska länder överst). Om de INTE
  // finns (t.ex. levereras inte av /Fields-endpointen) renderas hellre ett vanligt textfält än en
  // tom droplista. Ingen ny backend/endpoint införs för landslistan.
  const renderCountry = (field: FormSettingField): ReactNode => {
    const countryItems = toSelectableItems(field.extraOptions);

    if (countryItems.length > 0) {
      const sorted = [...countryItems].sort((a, b) => {
        const ai = PRIORITY_COUNTRIES.indexOf(a.value);
        const bi = PRIORITY_COUNTRIES.indexOf(b.value);
        if (ai !== -1 && bi !== -1) return ai - bi;
        if (ai !== -1) return -1;
        if (bi !== -1) return 1;
        return a.label.localeCompare(b.label, 'sv');
      });
      return selectBox('country', labelFor(field, 'country'), sorted);
    }

    // TODO: /Fields-endpointen (getFormSettingFields) levererar inga landsalternativ i extraOptions,
    // så land renderas som textfält mot 'country' (befintligt filter). Byt till droplistan ovan om/när
    // landslistan finns i fältets extraOptions – utan att införa ny backend.
    return <ControlledTextField name='country' label={labelFor(field, 'country')} />;
  };

  // Renderar ett fält utifrån dess typ (steg 3, när inget fieldName-specialfall matchar).
  const renderByType = (field: FormSettingField): ReactNode => {
    const name = field.fieldName;
    const rhfName = NAME_MAP[name] ?? name;

    // Land hanteras separat (droplista med textfält-fallback) och har alltid ett filter.
    if (field.type === FormSettingsTypeEnum.Country) return renderCountry(field);

    // Generiska fält kräver ett befintligt backend-filter, annars renderas de inte.
    if (!FILTER_FIELDS.has(rhfName)) return null;

    switch (field.type) {
      case FormSettingsTypeEnum.Dropdown: {
        // Droplista bara om alternativ finns (extraOptions), annars textfält mot samma filter.
        const items = toSelectableItems(field.extraOptions);
        return items.length > 0 ? (
          selectBox(rhfName, labelFor(field), items)
        ) : (
          <ControlledTextField name={rhfName} label={labelFor(field, rhfName)} />
        );
      }
      case FormSettingsTypeEnum.Checkbox:
      case FormSettingsTypeEnum.CheckboxRadio:
        // TODO: Standard-checkboxfält saknar ett rimligt sök-filter och hoppas över här. Tidning är
        // undantaget och hanteras som specialfält (har/saknar-toggle) ovan.
        return null;
      case FormSettingsTypeEnum.Text:
      case FormSettingsTypeEnum.Multiline:
      case FormSettingsTypeEnum.PostNr:
        return text(field, rhfName);
      default:
        // Övriga textliknande typer (Email, Phone m.fl.) värdesöks som text mot sitt fieldName.
        return text(field, rhfName);
    }
  };

  // Iterera formulärets fält i formulärets ordning (rad, sedan kolumn) så sökfälten ligger i samma
  // ordning som medlemsformuläret. Län/kommun och distrikt renderas EN gång via gemensamma väljare.
  const items: ReactNode[] = [];
  let countyRendered = false;

  // Läns-/kommun-väljaren. municipalityShown styr om kommun-nivån visas i väljaren (annars bara län).
  // Etiketten kombineras av formulärets läns- och kommun-rubriker (t.ex. "Län och Kommun") för de nivåer
  // som är påslagna; är ingen rubrik satt faller komponenten tillbaka på sin i18n-standard.
  const municipalityShown = fields.get('municipalityCode')?.show === true;
  const countyShown = fields.get('countyCode')?.show === true;
  const countyLabel =
    [countyShown ? fields.get('countyCode')?.heading?.trim() : '', municipalityShown ? fields.get('municipalityCode')?.heading?.trim() : '']
      .filter((x) => !!x)
      .join(' och ') || undefined;

  const sortedFields = Array.from(fields.values()).sort((a, b) => (a.row ?? 0) - (b.row ?? 0) || (a.col ?? 0) - (b.col ?? 0));

  sortedFields.forEach((field) => {
    if (!field.show) return;
    const name = field.fieldName;

    // 1. Fält som aldrig renderas (fieldName eller typ).
    if (SKIP_FIELD_NAMES.has(name)) return;
    if (SKIP_TYPES.has(field.type)) return;

    let controlNode: ReactNode = null;

    // 2. Specialfält (styrs av fieldName, före typ).
    if (name === 'countyCode' || name === 'municipalityCode') {
      if (countyRendered) return;
      countyRendered = true;
      controlNode = <MemberAdvancedSearchCounty lanOnly={!municipalityShown} label={countyLabel} />;
    } else if (name === 'sex') {
      controlNode = <MemberAdvancedSearchGender />;
    } else if (name === 'birthDate') {
      // Personnummer lagras i Member-kolumnen birthDate; sök via RHF 'pnr'.
      controlNode = text(field, 'pnr');
    } else if (CUSTOM_FIELD_REGEX.test(name)) {
      controlNode = renderCustomField(field);
    } else if (NEWSPAPER_REGEX.test(name)) {
      // Tidning renderas efter formulärets typ (som "Redigera medlem"): checkbox-konfig → har/saknar-
      // toggle (befintligt hasNewspaper-filter), text-konfig → textruta mot newspaper-värdefiltret.
      if (field.type === FormSettingsTypeEnum.Checkbox || field.type === FormSettingsTypeEnum.CheckboxRadio) {
        controlNode = (
          <MemberAdvancedSearchExclusiveToggleButton
            nameProp='hasNewspaper'
            label={field.heading?.trim() || t('member:memberAdvancedSearch.hasNewspaper.title')}
            truthyLabel={t('common:button.yes')}
            falsyLabel={t('common:button.no')}
          />
        );
      } else {
        controlNode = text(field, 'newspaper');
      }
    } else {
      // 3. Fältet styrs av sin typ.
      controlNode = renderByType(field);
    }

    if (!controlNode) return;

    items.push(
      <Grid item {...fieldGrid} key={name}>
        {controlNode}
      </Grid>,
    );
  });

  return <>{items}</>;
};

export default MemberAdvancedSearchMemberData;
